import { z } from "zod";

export const prenatalIntakeFormSchema = z.object({
  // Section I: General Information
  first_name: z.string().min(1, "First name is required").max(100),
  last_name: z.string().min(1, "Last name is required").max(100),
  middle_initial: z.string().max(5).optional(),
  address_line1: z.string().min(1, "Address is required").max(200),
  address_line2: z.string().max(200).optional(),
  city: z.string().min(1, "City is required").max(100),
  state: z.string().min(2, "State is required").max(2),
  zip_code: z.string().min(5, "ZIP code is required").max(10),
  county: z.string().max(100).optional(),
  phone: z.string().min(10, "Valid phone number required").max(15),
  email: z.string().email("Invalid email").optional().or(z.literal("")),
  preferred_contact_method: z.string().optional(),
  preferred_contact_time: z.string().optional(),
  medicaid_id: z.string().max(20).optional(),
  date_of_birth: z.string().min(1, "Date of birth is required"),
  ethnicity: z.string().optional(),
  race: z.array(z.string()).default([]),
  education_level: z.string().optional(),
  marital_status: z.string().optional(),
  emergency_contact_name: z.string().min(1, "Emergency contact name is required").max(100),
  emergency_contact_phone: z.string().min(10, "Valid phone number required").max(15),

  // Section II: Current Pregnancy
  is_multiple_pregnancy: z.boolean().default(false),
  due_date: z.string().optional(),
  first_prenatal_appointment: z.string().optional(),
  has_scheduled_appointment: z.boolean().optional(),
  receiving_wic: z.boolean().optional(),
  weight_before_pregnancy: z.string().optional(),
  current_weight: z.string().optional(),
  height: z.string().optional(),
  pregnancy_going_well_notes: z.string().max(1000).optional(),
  pregnancy_goals: z.string().max(1000).optional(),
  pregnancy_timing_preference: z.string().optional(),
  planning_to_breastfeed: z.string().optional(),
  breastfeeding_knowledge: z.string().max(1000).optional(),
  bleeding_or_cramping: z.boolean().optional(),
  bleeding_cramping_notes: z.string().max(500).optional(),

  // Section III: Pregnancy History
  is_first_pregnancy: z.boolean().optional(),
  children_in_care: z.number().min(0).default(0),
  living_children_born: z.number().min(0).default(0),
  full_term_births: z.number().min(0).default(0),
  premature_births: z.number().min(0).default(0),
  miscarriages_after_20_weeks: z.number().min(0).default(0),
  miscarriages_before_20_weeks: z.number().min(0).default(0),
  low_birth_weight_babies: z.number().min(0).default(0),
  high_birth_weight_babies: z.number().min(0).default(0),
  last_pregnancy_end_date: z.string().optional(),
  last_pregnancy_outcome: z.string().optional(),

  // Section IV: Health Information
  has_pcp: z.boolean().optional(),
  pcp_name: z.string().max(100).optional(),
  pcp_phone: z.string().max(15).optional(),
  health_conditions: z.array(z.string()).default([]),
  other_health_conditions: z.string().max(500).optional(),
  sti_screened: z.boolean().optional(),
  dental_visits_last_two_years: z.number().min(0).optional(),
  dental_issues: z.boolean().optional(),
  dental_issues_notes: z.string().max(500).optional(),

  // Mental Health and Substance Use
  tobacco_before_pregnancy: z.boolean().optional(),
  tobacco_before_pregnancy_notes: z.string().max(500).optional(),
  tobacco_during_pregnancy: z.boolean().optional(),
  tobacco_during_pregnancy_notes: z.string().max(500).optional(),
  household_tobacco_use: z.boolean().optional(),
  alcohol_before_pregnancy: z.boolean().optional(),
  alcohol_before_pregnancy_amount: z.string().max(200).optional(),
  alcohol_during_pregnancy: z.boolean().optional(),
  alcohol_during_pregnancy_amount: z.string().max(200).optional(),
  non_prescribed_drug_use: z.boolean().optional(),
  feeling_depressed: z.boolean().optional(),
  stress_level: z.string().optional(),
  mental_health_concerns: z.boolean().optional(),
  mental_health_concerns_notes: z.string().max(1000).optional(),

  // Environmental and Social Factors
  housing_concerns: z.boolean().optional(),
  feels_safe_at_home: z.boolean().optional(),
  food_insecurity: z.boolean().optional(),
  transportation_barriers: z.boolean().optional(),
  history_of_abuse: z.boolean().optional(),
  has_support_system: z.boolean().optional(),
  support_system_details: z.string().max(1000).optional(),

  // Member Needs/Worries
  worries: z.array(z.string()).default([]),
  primary_worry: z.string().max(500).optional(),
  coping_strategies: z.string().max(1000).optional(),
  topics_to_learn: z.array(z.string()).default([]),
  additional_notes: z.string().max(2000).optional(),

  // Eligibility
  is_english_fluent: z.boolean().optional(),

  // Consent
  consent_given: z.boolean().refine((val) => val === true, {
    message: "You must agree to the terms to submit this form",
  }),
  member_signature: z.string().min(1, "Signature is required").max(100),
  
  // Visit Preferences
  visit_location_preference: z.array(z.string()).default([]),
  communication_consent: z.array(z.string()).default([]),
});

export type PrenatalIntakeFormData = z.infer<typeof prenatalIntakeFormSchema>;

export const US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
  "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY"
];

export const RACE_OPTIONS = [
  { value: "american_indian", label: "American Indian / Alaska Native" },
  { value: "asian", label: "Asian" },
  { value: "black", label: "Black / African American" },
  { value: "hawaiian", label: "Hawaiian / Pacific Islander" },
  { value: "white", label: "White" },
  { value: "other", label: "Other" },
];

export const HEALTH_CONDITIONS = [
  { value: "asthma", label: "Asthma" },
  { value: "high_blood_pressure", label: "High Blood Pressure / Hypertension" },
  { value: "chlamydia_gonorrhea_syphilis", label: "Chlamydia, Gonorrhea, Syphilis, or Genital Herpes" },
  { value: "seizures_epilepsy", label: "Seizures or Epilepsy" },
  { value: "uti", label: "Urinary Tract Infection" },
  { value: "diabetes", label: "Diabetes" },
  { value: "other", label: "Other Illness or Condition" },
];

export const WORRY_OPTIONS = [
  { value: "money_problems", label: "Money problems" },
  { value: "labor_delivery", label: "Labor and delivery" },
  { value: "own_job_education", label: "Their own job, unemployment, or education" },
  { value: "caring_for_baby", label: "Caring for this baby" },
  { value: "partner_job", label: "Their partner's job or unemployment" },
  { value: "caring_for_other_children", label: "Caring for their other children" },
  { value: "own_substance_use", label: "Their own drinking or substance use" },
  { value: "stable_housing_food", label: "Stable housing / food" },
  { value: "household_substance_use", label: "Drinking or substance use by someone else in their household" },
  { value: "accessing_services", label: "Difficulty accessing medical or social service support" },
  { value: "relationship_with_partner", label: "Their relationship with their partner" },
  { value: "partner_didnt_want_pregnancy", label: "Their partner didn't want this pregnancy" },
  { value: "transportation", label: "Access to transportation" },
];

export const TOPICS_TO_LEARN = [
  { value: "alcohol_effect", label: "Alcohol's effect on their health and their baby's health" },
  { value: "stop_tobacco", label: "How to stop using tobacco products" },
  { value: "baby_growth", label: "Baby growth and development" },
  { value: "pregnancy_comfort", label: "How to be more comfortable during the pregnancy" },
  { value: "breastfeeding", label: "Breastfeeding" },
  { value: "caring_for_newborn", label: "Caring for their newborn" },
  { value: "nutrition", label: "Nutrition during and after the pregnancy" },
  { value: "family_planning", label: "Family planning and birth control" },
  { value: "managing_stress", label: "Managing stress" },
  { value: "getting_healthcare", label: "Getting health care for themselves or their baby" },
];

// Risk factor calculation - members with 4+ risk factors or under 18 are eligible
export function calculateRiskFactors(data: Partial<PrenatalIntakeFormData>): number {
  let riskFactors = 0;
  
  // Age under 18 (handled separately for eligibility)
  // Race-based disparities (risk factor)
  if (data.race?.includes("black") || data.race?.includes("american_indian")) riskFactors++;
  
  // Education
  if (data.education_level === "did_not_complete_high_school") riskFactors++;
  
  // Marital status
  if (data.marital_status === "not_married") riskFactors++;
  
  // Multiple pregnancy
  if (data.is_multiple_pregnancy) riskFactors++;
  
  // Pregnancy history
  if ((data.premature_births ?? 0) > 0) riskFactors++;
  if ((data.miscarriages_after_20_weeks ?? 0) > 0) riskFactors++;
  if ((data.miscarriages_before_20_weeks ?? 0) > 0) riskFactors++;
  if ((data.low_birth_weight_babies ?? 0) > 0) riskFactors++;
  
  // Health conditions
  if ((data.health_conditions?.length ?? 0) > 0) riskFactors++;
  
  // Tobacco use
  if (data.tobacco_before_pregnancy || data.tobacco_during_pregnancy) riskFactors++;
  if (data.household_tobacco_use) riskFactors++;
  
  // Alcohol use
  if (data.alcohol_before_pregnancy || data.alcohol_during_pregnancy) riskFactors++;
  
  // Drug use
  if (data.non_prescribed_drug_use) riskFactors++;
  
  // Mental health
  if (data.feeling_depressed) riskFactors++;
  if (data.stress_level === "high") riskFactors++;
  if (data.mental_health_concerns) riskFactors++;
  
  // Social/environmental
  if (data.housing_concerns) riskFactors++;
  if (data.feels_safe_at_home === false) riskFactors++;
  if (data.food_insecurity) riskFactors++;
  if (data.transportation_barriers) riskFactors++;
  if (data.history_of_abuse) riskFactors++;
  if (data.has_support_system === false) riskFactors++;
  
  // Language barrier
  if (data.is_english_fluent === false) riskFactors++;
  
  return riskFactors;
}
